import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import {
  assumirAtendimento,
  devolverAoSecretario,
  fetchConversaDetalhe,
  ignorarNumeroDoContato,
  registrarDesfecho,
  registrarRetorno,
  type ConversaDetalhe,
} from '@/api/secretario';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/skeleton';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { Acoes, acoesDoEstado, Cabecalho, FaixaDeAtencao } from './atendimento/Cabecalho';
import { NumeroDoContato } from './atendimento/NumeroDoContato';
import { Caso } from './atendimento/Caso';
import { Conversa, LinhaDoTempo } from './atendimento/Conversa';
import { EncerrarDialog, RetornoDialog, VincularProcessoDialog } from './atendimento/Dialogos';
import { Entregas } from './atendimento/Entregas';
import { QuemCuida } from './atendimento/QuemCuida';

const VOLTAR = '/app/secretario-ia?tab=atendimentos';

/**
 * O dossiê do atendimento (docs/PLANO_SECRETARIO_ATENDIMENTOS.md): o caso em cinco
 * linhas, o que o cliente entregou, o seguimento — e uma ação primária por
 * estado. É a página para onde o push e o e-mail apontam.
 */
export default function AtendimentoPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const [dialogo, setDialogo] = useState<'retorno' | 'encerrar' | 'vincular' | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.secretario.atendimento(id),
    queryFn: () => fetchConversaDetalhe(id),
    enabled: id !== '',
  });

  const invalidar = [
    [...queryKeys.secretario.atendimento(id)],
    [...queryKeys.secretario.atendimentosAll],
    [...queryKeys.secretario.contagensAll],
  ];
  const aoConcluir = (mensagem: string) => () => {
    setDialogo(null);
    showSuccess(mensagem);
  };

  const retorno = useOfflineMutation<ConversaDetalhe, { observacao: string }>({
    mutationFn: ({ observacao }) => registrarRetorno(id, observacao || null),
    outbox: {
      type: 'secretario_retorno',
      getPayload: ({ observacao }) => ({ method: 'POST', url: `/secretario/conversas/${id}/retorno`, body: { observacao: observacao || null } }),
      getDescription: () => 'Registrar retorno ao cliente',
    },
    invalidateKeys: invalidar,
    onSuccess: aoConcluir('Retorno registrado. O cliente que perguntar ouve que você já entrou em contato.'),
    onQueued: aoConcluir('Retorno registrado; será sincronizado ao reconectar.'),
    onError: (err) => notifyMutationError(err, showError),
  });
  const assumir = useOfflineMutation<ConversaDetalhe, void>({
    mutationFn: () => assumirAtendimento(id),
    outbox: {
      type: 'secretario_assumir',
      getPayload: () => ({ method: 'POST', url: `/secretario/conversas/${id}/assumir` }),
      getDescription: () => 'Assumir atendimento escalado',
    },
    invalidateKeys: invalidar,
    onSuccess: aoConcluir('Você assumiu este atendimento.'),
    onQueued: aoConcluir('Assumido; será sincronizado ao reconectar.'),
    onError: (err) => notifyMutationError(err, showError),
  });
  const desfecho = useOfflineMutation<ConversaDetalhe, { tipo: 'PROCESSO' | 'ENCERRADA'; motivo?: string; processo_id?: string }>({
    mutationFn: (input) => registrarDesfecho(id, input),
    outbox: {
      type: 'secretario_desfecho',
      getPayload: (input) => ({ method: 'POST', url: `/secretario/conversas/${id}/desfecho`, body: input }),
      getDescription: (input) => (input.tipo === 'PROCESSO' ? 'Vincular atendimento a processo' : 'Encerrar atendimento'),
    },
    invalidateKeys: invalidar,
    onSuccess: (_d, input) => aoConcluir(input.tipo === 'PROCESSO' ? 'Atendimento vinculado ao processo.' : 'Atendimento encerrado.')(),
    onQueued: aoConcluir('Desfecho registrado; será sincronizado ao reconectar.'),
    onError: (err) => notifyMutationError(err, showError),
  });
  const devolver = useOfflineMutation<ConversaDetalhe, void>({
    mutationFn: () => devolverAoSecretario(id),
    outbox: {
      type: 'secretario_devolver',
      getPayload: () => ({ method: 'POST', url: `/secretario/conversas/${id}/devolver` }),
      getDescription: () => 'Devolver o contato ao Secretário',
    },
    invalidateKeys: invalidar,
    onSuccess: aoConcluir('Se a pessoa escrever de novo, o Secretário atende.'),
    onQueued: aoConcluir('Devolvido; será sincronizado ao reconectar.'),
    onError: (err) => notifyMutationError(err, showError),
  });
  const ignorar = useOfflineMutation<ConversaDetalhe, void>({
    mutationFn: () => ignorarNumeroDoContato(id),
    outbox: {
      type: 'secretario_ignorar_numero',
      getPayload: () => ({ method: 'POST', url: `/secretario/conversas/${id}/ignorar-numero` }),
      getDescription: () => 'O Secretário não atende este número',
    },
    invalidateKeys: invalidar,
    onSuccess: aoConcluir('O Secretário não atende mais este número.'),
    onQueued: aoConcluir('Registrado; será sincronizado ao reconectar.'),
    onError: (err) => notifyMutationError(err, showError),
  });
  const ocupado =
    retorno.isPending || assumir.isPending || desfecho.isPending || devolver.isPending || ignorar.isPending;

  if (isLoading) {
    return (
      <div className="space-y-4" role="status" aria-label="Carregando atendimento">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-28 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
      </div>
    );
  }
  if (error || !data) {
    const classified = error ? classifyError(error) : null;
    return (
      <ErrorState
        title={classified?.status === 404 ? 'Atendimento não encontrado' : 'Não foi possível carregar o atendimento'}
        description={classified?.message}
        actions={[
          { label: 'Voltar aos atendimentos', onClick: () => navigate(VOLTAR) },
          { label: 'Tentar novamente', onClick: () => refetch() },
        ]}
      />
    );
  }

  // Sem nome, o balão diz "Cliente": o primeiro pedaço do telefone formatado seria "(84)".
  const primeiroNome = data.conversa.contato_nome?.trim().split(' ')[0] || 'Cliente';
  const temAcoes = acoesDoEstado(data).length > 0;
  const acoes = (
    <Acoes
      dossie={data}
      ocupado={ocupado}
      onRetorno={() => setDialogo('retorno')}
      onAssumir={() => assumir.mutate()}
      onVincular={() => setDialogo('vincular')}
      onEncerrar={() => setDialogo('encerrar')}
    />
  );

  return (
    <div className={`space-y-4 ${temAcoes ? 'pb-24 sm:pb-0' : ''}`}>
      <Link to={VOLTAR} className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Atendimentos
      </Link>

      <Cabecalho dossie={data} />
      {/* No desktop as ações ficam ao lado do fato que as pede; no celular, na barra fixa embaixo. */}
      <FaixaDeAtencao dossie={data} acoes={temAcoes ? acoes : null} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
        <div className="space-y-4">
          <Caso questionario={data.questionario} />
          <Conversa mensagens={data.mensagens} primeiroNome={primeiroNome} />
          <LinhaDoTempo eventos={data.linha_do_tempo} />
        </div>
        <div className="space-y-4">
          <Entregas instrumentos={data.instrumentos} documentos={data.documentos} />
          <NumeroDoContato
            dossie={data}
            ocupado={ocupado}
            onDevolver={() => devolver.mutate()}
            onIgnorar={() => ignorar.mutate()}
          />
          <QuemCuida dossie={data} />
        </div>
      </div>

      {temAcoes && (
        <div data-testid="barra-de-acoes" className="fixed inset-x-0 bottom-0 z-10 border-t bg-card p-3 sm:hidden">
          {acoes}
        </div>
      )}

      <RetornoDialog
        open={dialogo === 'retorno'}
        onOpenChange={(open) => !open && setDialogo(null)}
        ocupado={ocupado}
        onConfirmar={(observacao) => retorno.mutate({ observacao })}
      />
      <EncerrarDialog
        open={dialogo === 'encerrar'}
        onOpenChange={(open) => !open && setDialogo(null)}
        ocupado={ocupado}
        onConfirmar={(motivo) => desfecho.mutate({ tipo: 'ENCERRADA', motivo })}
      />
      <VincularProcessoDialog
        open={dialogo === 'vincular'}
        onOpenChange={(open) => !open && setDialogo(null)}
        ocupado={ocupado}
        clienteId={data.cliente?.id ?? null}
        onConfirmar={(processo_id) => desfecho.mutate({ tipo: 'PROCESSO', processo_id })}
      />
    </div>
  );
}
